<!-- eslint-disable max-len -->
<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Staffing agency onboarding') }}
    </template>
    <template #header-description>
      <WdsText secondary>
        {{
          $t(
            'Require staffing agencies to review documents or provide details before they can review hire offers.',
          )
        }}
      </WdsText>
    </template>
  </PageHeader>
  <div class="tw-pt-4 tw-pb-4">
    <BaseCard>
      <template #card-header>
        <WdsStack direction-row align-center gap-sm>
          <WdsHeading type="title2">{{ $t('Settings') }}</WdsHeading>
          <EditSettingsOption
            v-model="requireRecruiterOnboarding"
            :label="$t('Require onboarding')"
            :loading="saving"
            :failed="savingFailed"
            @update:model-value="save('require_recruiter_onboarding', $event)"
          />
        </WdsStack>
      </template>
    </BaseCard>

    <WdsAlert v-if="!requireRecruiterOnboarding" warning icon class="tw-mt-4">
      {{ $t('Onboarding is off.') }}
      {{
        $t(
          'Staffing agencies are currently not asked to complete any additional requirements set by your company.',
        )
      }}
    </WdsAlert>

    <WdsAlert v-if="documentError" error icon class="tw-mt-4" @close="documentError = null">
      {{ documentError }}
    </WdsAlert>

    <BaseCard :has-header-padding="false" class="tw-mt-4">
      <template #card-header>
        <WdsStack direction-row justify-space-between align-center>
          <WdsHeading type="title2">{{ $t('Required documents') }}</WdsHeading>
          <div>
            <PickerFileFilebank
              v-slot="{ openFilePicker }"
              :exclude-ids="documents.map((doc) => doc.id)"
              :exclude-tags="['Contract Template']"
              :use-global-id="true"
              @files-selected="addDocuments"
            >
              <WdsButton
                primary
                data-test="document-add-staffing-agency-onboarding"
                @click="openFilePicker"
              >
                <WdsStack align-center gap-sm>
                  <WdsIcon name="add" :size="16" />
                  {{ $t('Add document') }}
                </WdsStack>
              </WdsButton>
            </PickerFileFilebank>
          </div>
        </WdsStack>
      </template>

      <WdsText secondary class="tw-mt-1">
        {{ $t('Add documents for invited staffing agencies to review and accept.') }}
      </WdsText>

      <div class="tw-w-full tw-pt-2">
        <div v-if="documents.length === 0" class="tw-py-4">
          <WdsText secondary>{{ $t('No documents added yet') }}</WdsText>
        </div>
        <WdsStack
          v-for="document in documents"
          :key="document.id"
          justify-space-between
          align-center
          class="tw-bg-white tw-p-3 tw-rounded tw-my-3"
        >
          <WdsStack align-center gap-sm>
            <WdsStack class="tw-mx-2">
              <WdsIcon name="document" :size="20" />
            </WdsStack>
            <WdsStack direction-column>
              <WdsText v-if="document.title !== null">{{ document.title }}</WdsText>
              <a
                :href="laravelNamedRoute(namedRoutes.files.download, { fileId: document.id })"
                class="tw-font-medium"
                target="_blank"
              >
                {{ document.name }}
              </a>
            </WdsStack>
          </WdsStack>
          <WdsStack align-center gap-sm>
            <WdsMenu z-index="50" placement="bottom-end" manual-trigger>
              <template #trigger="{ toggle }">
                <WdsButtonGhost @click="toggle">
                  <WdsIcon name="overflow-menu-horizontal" :size="16" />
                </WdsButtonGhost>
              </template>

              <WdsMenuOption @click="handleEditDocument(document)">
                <WdsIcon :size="16" name="edit" />
                {{ $t('Edit') }}
              </WdsMenuOption>
              <WdsTooltip
                v-if="document.recruitersHaveAccepted"
                left
                :text="
                  $t(
                    'This document has already been accepted by a staffing agency and cannot be deleted.',
                  )
                "
              >
                <WdsMenuOption disabled>
                  <WdsIcon :size="16" name="trash-can" />
                  {{ $t('Remove') }}
                </WdsMenuOption>
              </WdsTooltip>
              <WdsMenuOption v-else theme="DESTRUCTIVE" @click="handleRemoveDocument(document)">
                <WdsIcon :size="16" name="trash-can" />
                {{ $t('Remove') }}
              </WdsMenuOption>
            </WdsMenu>
          </WdsStack>
        </WdsStack>
      </div>
    </BaseCard>

    <BaseCard :has-header-padding="false" class="tw-mt-4">
      <template #card-header>
        <WdsStack direction-row justify-space-between align-center>
          <WdsHeading type="title2">{{ $t('Additional information') }}</WdsHeading>
          <a :href="customFieldsSettingsUrl">
            <WdsButton primary>
              <WdsStack align-center gap-sm>
                {{ $t('Manage custom fields') }}
                <WdsIcon name="arrow-right" :size="16" />
              </WdsStack>
            </WdsButton>
          </a>
        </WdsStack>
      </template>

      <WdsText secondary class="tw-mt-1">
        {{
          $t(
            'Here you can see all custom fields marked for "Staffing agency onboarding". All agencies joining your pool will be asked to fill them out.',
          )
        }}
      </WdsText>

      <div class="tw-w-full tw-pt-4">
        <div v-if="customFieldsLoading" class="tw-text-center tw-py-5">
          <WdsSpinner :indeterminate="true" />
        </div>
        <template v-else-if="customFields.length > 0">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="tw-text-left">{{ $t('Field name') }}</th>
                <th class="tw-text-center">{{ $t('Required') }}</th>
                <th class="tw-text-center">{{ $t('Field type') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="field in customFields" :key="field.id!">
                <td>{{ field.title }}</td>
                <td class="tw-text-center">{{ field.displayValidations() }}</td>
                <td class="tw-text-center">{{ getFieldTypeLabel(field.fieldType) }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <WdsText v-else secondary class="tw-py-4">
          {{ $t('No custom fields added for staffing agencies.') }}
        </WdsText>
      </div>
    </BaseCard>
  </div>

  <ModalFileEdit
    v-if="selectedFile"
    :file="selectedFile"
    :available-tags="availableTags"
    :is-saving="updatingFile"
    :is-open="selectedFile && isEditingFileMode"
    :show-tags="false"
    :show-title="true"
    @save="saveFileChanges"
    @canceled="cancelEditFile"
  />

  <ModalConfirmSwal
    v-if="isDeletionModalOpen"
    :header-text="$t('Are you sure?')"
    :text="deletionModalText"
    :confirm-button-text="$t('Yes, delete')"
    :processing-text="$t('Deleting')"
    @confirmed="confirmDeleteDocument"
    @cancelled="cancelDeleteDocument"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsText,
  WdsHeading,
  WdsStack,
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
  WdsTooltip,
  WdsAlert,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseCard from '@/components/BaseCard.vue'
import EditSettingsOption from '@/components/EditSettingsOption.vue'
import PageHeader from '@/components/PageHeader.vue'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import ModalConfirmSwal from '@/components/ModalConfirmSwal.vue'
import ModalFileEdit from '@/components/ModalFileEdit.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { CustomFieldsListQuery } from '@/components/Fields/graphql/queries'
import { CustomFieldListFragment } from '@/components/Fields/graphql/fragments'
import { useFragment } from '@/gql'
import { ContractDocType, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import {
  newInstanceOfCustomField,
  getFieldTypeLabel,
} from '@/components/Fields/utils/CustomFieldUtils'
import type { CustomField } from '@/components/Fields/dataObjects/CustomField'

interface Document {
  id: string
  name: string
  title: string
  recruitersHaveAccepted: boolean
}

const props = withDefaults(
  defineProps<{
    requireRecruiterOnboarding?: boolean
    initialDocuments?: Array<Document>
  }>(),
  {
    requireRecruiterOnboarding: false,
    initialDocuments: () => [],
  },
)

const requireRecruiterOnboarding = ref(props.requireRecruiterOnboarding)
const documents = ref(props.initialDocuments)

const { result: customFieldsResult, loading: customFieldsLoading } = useQuery(
  CustomFieldsListQuery,
  {
    accounts: [currentAccount.globalId],
    appliesTo: [TypeSupportingCustomFieldValues.CompanyRecruiter],
  },
  { clientId: 'publicApiCustomFields' },
)

const customFields = computed<CustomField[]>(
  () =>
    customFieldsResult.value?.customFields.data
      .map((field) => {
        const fieldTyped = useFragment(CustomFieldListFragment, field)
        return newInstanceOfCustomField(fieldTyped, fieldTyped.fieldType)
      })
      .filter((field) => field.recruiterInputAllowed) || [],
)

const customFieldsSettingsUrl = computed(
  () => `${laravelNamedRoute(namedRoutes.frontend.user.profile.edit)}#custom-fields`,
)
const saving = ref(false)
const savingFailed = ref(false)
const isDeletionModalOpen = ref(false)
const documentToDelete = ref<{ id: string; name: string } | null>(null)
const selectedFile = ref<{
  localId: string
  globalId: string
  name: string
  title: string
  extension: string
  tags: { name: string }[]
} | null>(null)
const isEditingFileMode = ref(false)
const updatingFile = ref(false)
const documentError = ref<string | null>(null)

const STAFFING_AGENCY_ONBOARDING_TAG = ContractDocType.StaffingAgencyOnboarding.toLowerCase()
const availableTags = ref([STAFFING_AGENCY_ONBOARDING_TAG])

const deletionModalText = computed(() => {
  if (!documentToDelete.value) return ''
  return $t('Do you want to permanently delete this document?')
})

const updateCompanyMutation = gql`
  mutation updateCompanyStaffingAgencyOnboarding($globalId: ID!, $settings: CompanySettingsInput!) {
    updateCompany(input: { globalId: $globalId, settings: $settings }) {
      globalId
      settings {
        require_recruiter_onboarding
      }
    }
  }
`

const addDocumentsMutation = gql`
  mutation manageRecruiterOnboardingDocuments($globalId: ID!, $documents: [ID!]!) {
    manageRecruiterOnboardingDocuments(input: { company: $globalId, documents: $documents }) {
      id
    }
  }
`

const removeDocumentsMutation = gql`
  mutation removeRecruiterOnboardingDocuments($globalId: ID!, $documents: [ID!]!) {
    removeRecruiterOnboardingDocuments(input: { company: $globalId, documents: $documents }) {
      id
    }
  }
`

const updateFileMutation = gql`
  mutation updateFile(
    $file: FileIntOrIDInput!
    $fileName: String!
    $tags: [String!]
    $title: String
  ) {
    updateFile(file: $file, fileName: $fileName, title: $title, tags: $tags) {
      globalId
      name
      title
      tags {
        name
      }
    }
  }
`

const deleteFilesMutation = gql`
  mutation DeleteFile($ids: [FileIntOrIDInput!]!) {
    deleteFiles(ids: $ids) {
      globalId
    }
  }
`

const { mutate: updateCompany } = useMutation(updateCompanyMutation, {
  clientId: 'default',
})

const { mutate: manageRecruiterOnboardingDocuments } = useMutation(addDocumentsMutation, {
  clientId: 'publicApi',
})

const { mutate: removeRecruiterOnboardingDocuments } = useMutation(removeDocumentsMutation, {
  clientId: 'publicApi',
})

const { mutate: updateFile } = useMutation(updateFileMutation, {
  clientId: 'default',
})

const { mutate: deleteFiles } = useMutation(deleteFilesMutation, {
  clientId: 'default',
})

const save = async (key: string, value: boolean) => {
  saving.value = true
  savingFailed.value = false

  try {
    await updateCompany({
      globalId: currentAccount.globalId,
      settings: {
        [key]: value,
      },
    })

    saving.value = false
  } catch {
    saving.value = false
    savingFailed.value = true
  }
}

const addDocuments = async (
  selectedDocuments: Array<{ id: string; name: string; title: string }>,
) => {
  await manageRecruiterOnboardingDocuments({
    globalId: currentAccount.globalId,
    documents: selectedDocuments.map((doc) => doc.id),
  })

  documents.value = [
    ...documents.value,
    ...selectedDocuments.map((doc) => ({
      ...doc,
      recruitersHaveAccepted: false,
    })),
  ]
}

const handleEditDocument = (document: Document) => {
  selectedFile.value = {
    localId: document.id,
    globalId: document.id,
    name: document.name,
    title: document.title,
    extension: document.name.split('.').pop() || '',
    tags: [{ name: STAFFING_AGENCY_ONBOARDING_TAG }],
  }
  isEditingFileMode.value = true
}

const cancelEditFile = () => {
  selectedFile.value = null
  isEditingFileMode.value = false
}

const saveFileChanges = async ([fileId, fileName, tags, title]: [
  string,
  string,
  string[],
  string,
]) => {
  updatingFile.value = true
  const normalizedTags = tags
    .filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)
    .map((tag) => tag.toLowerCase())

  try {
    const result = await updateFile({
      file: { globalId: fileId },
      fileName,
      tags: normalizedTags,
      title,
    })

    if (result && 'data' in result && result.data?.updateFile) {
      const updatedDoc = result.data.updateFile
      const index = documents.value.findIndex((doc) => doc.id === fileId)

      if (index !== -1) {
        const { recruitersHaveAccepted } = documents.value[index]
        documents.value[index] = {
          id: updatedDoc.globalId,
          name: updatedDoc.name,
          title: updatedDoc.title,
          recruitersHaveAccepted,
        }
      }
    }

    cancelEditFile()
    documentError.value = null
  } catch {
    documentError.value = $t('An error occurred while updating the document.')
  } finally {
    updatingFile.value = false
  }
}

const handleRemoveDocument = (document: Document) => {
  documentToDelete.value = document
  isDeletionModalOpen.value = true
}

const cancelDeleteDocument = () => {
  documentToDelete.value = null
  isDeletionModalOpen.value = false
}

const confirmDeleteDocument = async () => {
  if (!documentToDelete.value) return

  try {
    await removeRecruiterOnboardingDocuments({
      globalId: currentAccount.globalId,
      documents: [documentToDelete.value.id],
    })

    await deleteFiles({
      ids: [{ globalId: documentToDelete.value.id }],
    })

    documents.value = documents.value.filter((doc) => doc.id !== documentToDelete.value?.id)
    documentError.value = null
  } catch {
    documentError.value = $t('An error occurred while deleting the document.')
  } finally {
    cancelDeleteDocument()
  }
}
</script>

<style lang="scss" scoped>
.network-loader {
  margin-top: -7px;
}
</style>
